/**
 * Admin Simulation Component
 * Server-side simulation controls
 */

import { useEffect, useState, useCallback } from 'react';
import { LoadingSpinner } from '../ui';

interface SimulationData {
  state: string;
  tick: number;
  creatures: number;
  food: number;
  actualTPS: number;
  targetTPS: number;
  uptime: number;
  totalSpawns: number;
  worldSize: { width: number; height: number };
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export function AdminSimulation() {
  const [simulation, setSimulation] = useState<SimulationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSimulation = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/simulation`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch simulation');
      const data = await response.json();
      setSimulation(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching simulation');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSimulation();
    const interval = setInterval(fetchSimulation, 2000); // Refresh every 2s
    return () => clearInterval(interval);
  }, [fetchSimulation]);

  const handleAction = async (action: 'pause' | 'resume' | 'restart') => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/simulation/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to ${action} simulation`);
      await fetchSimulation();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Error: ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    return `${minutes}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <LoadingSpinner text="Cargando simulación..." />
      </div>
    );
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  if (!simulation) return null;

  const isRunning = simulation.state === 'running';
  const isPaused = simulation.state === 'paused';

  return (
    <div className="admin-simulation">
      <h2>Control de Simulación</h2>

      {/* Status Section */}
      <div className="admin-sim-section">
        <h3>Estado Actual</h3>
        <div className="admin-sim-stats">
          <div className="admin-sim-stat">
            <label>Estado</label>
            <span style={{ color: isRunning ? '#4ade80' : isPaused ? '#f59e0b' : '#dc2626' }}>
              {(simulation.state ?? 'unknown').toUpperCase()}
            </span>
          </div>
          <div className="admin-sim-stat">
            <label>TPS Actual</label>
            <span>{(simulation.actualTPS ?? 0).toFixed(1)} / {simulation.targetTPS ?? 30}</span>
          </div>
          <div className="admin-sim-stat">
            <label>Tick</label>
            <span>{(simulation.tick ?? 0).toLocaleString()}</span>
          </div>
          <div className="admin-sim-stat">
            <label>Uptime</label>
            <span>{formatUptime(simulation.uptime ?? 0)}</span>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="admin-sim-section">
        <h3>Controles</h3>
        <div className="admin-sim-controls">
          {isRunning ? (
            <button
              className="admin-sim-btn warning"
              onClick={() => handleAction('pause')}
              disabled={actionLoading}
            >
              {actionLoading ? '⏳ Procesando...' : '⏸ Pausar'}
            </button>
          ) : (
            <button
              className="admin-sim-btn primary"
              onClick={() => handleAction('resume')}
              disabled={actionLoading}
            >
              {actionLoading ? '⏳ Procesando...' : '▶ Reanudar'}
            </button>
          )}
          <button
            className="admin-sim-btn danger"
            onClick={() => {
              if (confirm('¿Estás seguro de reiniciar la simulación? Se perderán todas las criaturas actuales.')) {
                handleAction('restart');
              }
            }}
            disabled={actionLoading}
          >
            {actionLoading ? '⏳ Reiniciando...' : '🔄 Reiniciar Simulación'}
          </button>
        </div>
      </div>

      {/* World Stats */}
      <div className="admin-sim-section">
        <h3>Estadísticas del Mundo</h3>
        <div className="admin-sim-stats">
          <div className="admin-sim-stat">
            <label>Criaturas</label>
            <span>{simulation.creatures ?? 0}</span>
          </div>
          <div className="admin-sim-stat">
            <label>Comida</label>
            <span>{simulation.food ?? 0}</span>
          </div>
          <div className="admin-sim-stat">
            <label>Total Generados</label>
            <span>{(simulation.totalSpawns ?? 0).toLocaleString()}</span>
          </div>
          <div className="admin-sim-stat">
            <label>Tamaño del Mundo</label>
            <span>{simulation.worldSize?.width ?? 0} x {simulation.worldSize?.height ?? 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
